import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Timesheets (Kloepfer K1-8, 2026-09-23): the office's view of everyone's
// week. Rows come drawn from the server; this filters them, opens a person's
// entries and history, and sends Approve / Send back / Accept / employee
// numbers / crews back, redrawing just the row that changed.
export default class extends Controller {
  static targets = ["row", "body", "empty", "search", "crewFilter", "statusFilter", "crewMode", "crewSwitch", "crewSwitchRow",
    "sendBackModal", "sendBackNote", "sendBackWho", "crewsModal", "crewsList", "newCrew", "toast", "totals", "companyTotals", "dayModal", "dayPanel",
    "pickAll", "pickBar", "pickCount", "pickHours", "pickApprove"]
  static values = {
    week: String, personUrl: String, approveUrl: String, rejectUrl: String,
    acceptUrl: String, approveEditsUrl: String, askUrl: String, dayUrl: String, crewsUrl: String, crewModeUrl: String, messages: Object
  }

  connect() {
    this.localizeTimes(this.element)
    this.crewsChanged = false
    this.updateTotals()
    this.updatePicks()
  }

  // ── Filters: search, crew and status, all in the page ──────────────────
  filter() {
    const q = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()
    const crew = this.hasCrewFilterTarget ? this.crewFilterTarget.value : ""
    const status = this.hasStatusFilterTarget ? this.statusFilterTarget.value : "all"
    let shown = 0
    this.rowTargets.forEach(row => {
      const d = row.dataset
      let ok = !q || (d.search || "").includes(q)
      // Crews picked at clock-in (K2-5): the crews worked that week, else the usual crew.
      const worked = (d.workedCrewIds || "").split(" ").filter(Boolean)
      const ids = worked.length ? worked : (d.crewId ? [d.crewId] : [])
      if (ok && crew) ok = crew === "none" ? ids.length === 0 : ids.includes(crew)
      if (ok && status !== "all") {
        ok = status === "mine" ? d.mine === "true" : status === "needs_ok" ? d.needsOk === "true" : d.status === status
      }
      row.classList.toggle("hidden", !ok)
      const detail = this.detailFor(d.userId)
      if (detail && !ok) detail.classList.add("hidden")
      if (ok) shown++
    })
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", shown > 0)
    this.updateTotals()
    // A week the filters hide is never approved by the bar.
    this.rowTargets.forEach(row => {
      const box = this.pickBox(row)
      if (box && row.classList.contains("hidden")) box.checked = false
    })
    this.updatePicks()
  }

  // ── Everyone shown, day by day (Kloepfer K3-24, 2026-10-08) ────────────
  // The bottom row adds up the rows the filters leave on screen; the whole
  // company's row under it shows only when the two differ.
  updateTotals() {
    if (!this.hasTotalsTarget) return
    const visible = this.rowTargets.filter(row => !row.classList.contains("hidden"))
    let shownTotal = 0
    this.totalsTarget.querySelectorAll("[data-sum]").forEach(cell => {
      const key = cell.dataset.sum
      const seconds = visible.reduce((sum, row) => {
        const value = row.querySelector(`[data-sum-value="${key}"]`)
        return sum + Number(value?.dataset.seconds || 0)
      }, 0)
      if (key === "total") shownTotal = seconds
      cell.textContent = this.hoursLabel(seconds)
    })
    if (this.hasCompanyTotalsTarget) {
      const companyTotal = Number(this.companyTotalsTarget.dataset.totalSeconds || 0)
      this.companyTotalsTarget.classList.toggle("hidden", Math.abs(companyTotal - shownTotal) < 60)
    }
  }

  // The page's own "12.5" (or "12,5"): two decimals at most, a dash for nothing.
  hoursLabel(seconds) {
    const hours = seconds / 3600
    if (hours < 0.005) return "—"
    const lang = document.documentElement.lang || undefined
    return new Intl.NumberFormat(lang, { maximumFractionDigits: 2, useGrouping: false }).format(hours)
  }

  // ── A person's entries and history, under their row ────────────────────
  async toggle(event) {
    await this.toggleRow(event.target.closest("tr"))
  }

  async toggleRow(row) {
    if (!row) return
    const detail = this.detailFor(row.dataset.userId)
    if (!detail) return
    const open = detail.classList.contains("hidden")
    detail.classList.toggle("hidden", !open)
    row.querySelector("[data-name-toggle]")?.setAttribute("aria-expanded", String(open))
    if (open) await this.loadDetail(row.dataset.userId)
  }

  // One day at a time (Kloepfer K3-23): each day opens on its own.
  toggleDay(event) {
    const section = event.currentTarget.closest("[data-day]")
    const body = section?.querySelector("[data-day-body]")
    if (body) this.setDay(section, body.hidden)
  }

  toggleAllDays(event) {
    const button = event.currentTarget
    const root = button.closest("[data-detail-body]") || this.element
    const sections = [...root.querySelectorAll("[data-day]")]
    const open = sections.some(section => section.querySelector("[data-day-body]")?.hidden)
    sections.forEach(section => this.setDay(section, open))
    button.textContent = open ? button.dataset.closeLabel : button.dataset.openLabel
  }

  setDay(section, open) {
    const body = section.querySelector("[data-day-body]")
    if (!body) return
    body.hidden = !open
    section.querySelector(":scope > button")?.setAttribute("aria-expanded", String(open))
    section.querySelector("[data-day-chevron]")?.classList.toggle("rotate-90", open)
  }

  async loadDetail(userId) {
    const body = this.detailFor(userId)?.querySelector("[data-detail-body]")
    if (!body) return
    body.innerHTML = `<p class="text-[13px] text-gray-500">${this.escape(this.messagesValue.loading || "…")}</p>`
    try {
      const url = `${this.personUrlValue}?user_id=${encodeURIComponent(userId)}&week=${encodeURIComponent(this.weekValue)}`
      const res = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!res.ok) throw new Error(String(res.status))
      body.innerHTML = await res.text()
      this.localizeTimes(body)
    } catch (_e) {
      body.innerHTML = `<p class="text-[13px] text-red-600">${this.escape(this.messagesValue.failed || "")}</p>`
    }
  }

  // ── One day in a pop-up (Phelipe 2026-10-27) ──────────────────────────
  // A day's hours, or the hand beside them, open that day: the day on the
  // left (time, breadcrumbs, history) and on the right a map of where the
  // phone was, or only the changes made by hand. A click on a time, a stop or
  // a change shows it on the right.
  async openDay(event) {
    const button = event.currentTarget
    const userId = button.dataset.userId || button.closest("tr")?.dataset.userId
    const date = button.dataset.date
    if (!userId || !date || !this.hasDayPanelTarget) return
    if (button.closest("tr")) this.dayOpener = button
    const sel = button.dataset.focus === "edits" ? "edits" : "overview"
    await this.loadDay(userId, date, sel, button.dataset.from || (sel === "edits" ? "hand" : "hours"))
  }

  // Draws a day, or draws it again after a decision: the same day keeps what
  // the right side shows and where each side was scrolled.
  async loadDay(userId, date, sel, from) {
    const current = this.dayPopup
    const same = !!current && current.dataset.userId === String(userId) && current.dataset.date === date
    const sides = () => [...this.dayPanelTarget.querySelectorAll("[data-day-left], [data-day-right]")]
    const scrolls = same ? sides().map(el => el.scrollTop) : null
    if (!same) this.dayPanelTarget.innerHTML = `<p class="p-6 text-[13px] text-gray-500">${this.escape(this.messagesValue.loading || "…")}</p>`
    this.dayModalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    try {
      const edits = sel === "edits" || String(sel).startsWith("edit:")
      const query = new URLSearchParams({ user_id: userId, date, focus: edits ? "edits" : "overview" })
      if (from) query.set("from", from)
      const res = await fetch(`${this.dayUrlValue}?${query}`, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!res.ok) throw new Error(String(res.status))
      this.dayPanelTarget.innerHTML = await res.text()
      this.localizeTimes(this.dayPanelTarget)
      this.drawTimeBars(this.dayPanelTarget)
      this.pick(sel, { send: false, scroll: !same })
      if (scrolls) sides().forEach((el, i) => { el.scrollTop = scrolls[i] || 0 })
      else this.dayPanelTarget.querySelector("[data-day-close]")?.focus()
    } catch (_e) {
      this.dayPanelTarget.innerHTML = `<p class="p-6 text-[13px] text-red-600">${this.escape(this.messagesValue.failed || "")}</p>`
    }
  }

  closeDay() {
    if (!this.hasDayModalTarget || this.dayModalTarget.classList.contains("hidden")) return
    this.dayModalTarget.classList.add("hidden")
    this.dayPanelTarget.innerHTML = ""
    document.body.classList.remove("overflow-hidden")
    this.dayOpener?.focus?.()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  get dayPopup() {
    return this.hasDayPanelTarget ? this.dayPanelTarget.querySelector("[data-day-popup]") : null
  }

  // A click on either side: whatever is marked data-pick shows on the right.
  // The buttons and links inside a card keep doing their own thing.
  pickFrom(event) {
    const el = event.target.closest("[data-pick]")
    if (!el || !event.currentTarget.contains(el)) return
    const inner = event.target.closest("button, a, input, textarea, select, label")
    if (inner && inner !== el && el.contains(inner)) return
    this.pick(el.dataset.pick, { fromLeft: !!el.closest("[data-day-left]") })
  }

  pickKey(event) {
    if (event.key !== "Enter" && event.key !== " ") return
    const el = event.target
    if (!el.matches?.("[data-pick]") || el.matches("button")) return
    event.preventDefault()
    this.pick(el.dataset.pick, { fromLeft: !!el.closest("[data-day-left]") })
  }

  // A stop or a clock pin clicked on the map.
  mapPicked(event) {
    this.pick(event.detail?.sel)
  }

  pick(sel, { send = true, scroll = true, fromLeft = false } = {}) {
    const popup = this.dayPopup
    if (!popup || !sel) return
    let edits = sel === "edits" || sel.startsWith("edit:")
    if (edits && !popup.querySelector('[data-day-pane="edits"]')) {
      sel = "overview"
      edits = false
    }
    popup.dataset.sel = sel
    // On the left, what the right side shows stays outlined; a clock-in or
    // clock-out outlines its time entry.
    const entry = sel.startsWith("clock:") ? `entry:${sel.split(":")[1]}` : null
    popup.querySelectorAll("[data-day-left] [data-pick]").forEach(el => {
      const on = el.dataset.pick === sel || el.dataset.pick === entry
      el.style.outline = on ? "2px solid var(--color-brand)" : ""
      el.style.outlineOffset = on ? "1px" : ""
      if (on) el.setAttribute("aria-current", "true")
      else el.removeAttribute("aria-current")
    })
    popup.querySelectorAll("[data-day-tab]").forEach(tab => tab.setAttribute("aria-selected", String((tab.dataset.dayTab === "edits") === edits)))
    popup.querySelectorAll("[data-day-pane]").forEach(pane => { pane.hidden = (pane.dataset.dayPane === "edits") !== edits })
    if (edits) {
      popup.querySelectorAll("[data-edit-card]").forEach(card => {
        const on = sel === `edit:${card.dataset.editCard}`
        card.style.outline = on ? "2px solid var(--color-brand)" : ""
        if (on && scroll) card.scrollIntoView?.({ block: "nearest" })
      })
    } else {
      const key = entry || sel
      const cards = [...popup.querySelectorAll("[data-info-for]")]
      const shown = cards.some(card => card.dataset.infoFor === key) ? key : "overview"
      cards.forEach(card => { card.hidden = card.dataset.infoFor !== shown })
      popup.querySelector('[data-controller~="timesheet-day-map"]')?.dispatchEvent(new CustomEvent("timesheet-day-map:pick", { detail: { sel } }))
    }
    if (send) track("Timesheet Day Detail Viewed", { kind: sel.split(":")[0] })
    // On a phone the two sides stack: bring the right side into view.
    if (fromLeft && window.matchMedia?.("(max-width: 767px)")?.matches) popup.querySelector("[data-day-right]")?.scrollIntoView?.({ block: "start" })
  }

  // A hand change's day on a line: the phone's stops (green at the job,
  // amber away) over the time on the clock (blue), the part changed by hand
  // in red, with hour marks in the viewer's own time zone.
  drawTimeBars(root) {
    root.querySelectorAll("[data-time-bar]").forEach(bar => {
      let data
      try { data = JSON.parse(bar.dataset.timeBar) } catch (_e) { return }
      const at = value => (value ? new Date(value).getTime() : NaN)
      const all = [...(data.clock || []), ...(data.changed || []), ...(data.stops || [])]
        .flatMap(([a, b]) => [at(a), at(b)]).filter(Number.isFinite)
      if (!all.length) return
      const start = new Date(Math.min(...all) - 30 * 60000)
      start.setMinutes(0, 0, 0)
      const end = new Date(Math.max(...all) + 30 * 60000)
      end.setMinutes(60, 0, 0)
      const from = start.getTime()
      const to = end.getTime()
      const pct = t => ((Math.min(Math.max(t, from), to) - from) / (to - from)) * 100
      const lane = name => bar.querySelector(`[data-bar-track="${name}"]`)
      const add = (into, a, b, css) => {
        if (!into || !Number.isFinite(a) || !Number.isFinite(b) || b <= a) return
        const seg = document.createElement("span")
        seg.className = `absolute inset-y-0 rounded-full ${css}`
        seg.style.left = `${pct(a)}%`
        seg.style.width = `${Math.max(pct(b) - pct(a), 0.8)}%`
        into.appendChild(seg)
      }
      ;(data.stops || []).forEach(([a, b, atJob]) => add(lane("stops"), at(a), at(b), atJob ? "bg-green-500" : "bg-amber-400"))
      ;(data.clock || []).forEach(([a, b]) => add(lane("clock"), at(a), b ? at(b) : Date.now(), "bg-blue-500"))
      ;(data.changed || []).forEach(([a, b]) => {
        const [x, y] = [at(a), at(b)].sort((p, q) => p - q)
        add(lane("clock"), x, y, "bg-red-500")
      })
      const ticks = bar.querySelector("[data-bar-ticks]")
      if (!ticks) return
      const hours = (to - from) / 3600000
      const step = (hours > 12 ? 3 : hours > 6 ? 2 : 1) * 3600000
      for (let t = from; t <= to; t += step) {
        const tick = document.createElement("span")
        tick.className = "absolute -translate-x-1/2 text-[13px] text-gray-400 whitespace-nowrap"
        tick.style.left = `${pct(t)}%`
        tick.textContent = new Date(t).toLocaleTimeString([], { hour: "numeric" })
        ticks.appendChild(tick)
      }
    })
  }

  // ── Ticked weeks (Phelipe 2026-10-28) ──────────────────────────────────
  // Tick boxes pick the waiting weeks; the bar at the bottom approves or
  // sends them back together. Status stays in the table, actions live there.
  pickBox(row) {
    return row.querySelector("input[data-pick-row]")
  }

  pickable() {
    return this.rowTargets.filter(row => !row.classList.contains("hidden") && this.pickBox(row) && !this.pickBox(row).disabled)
  }

  pickedRows() {
    return this.pickable().filter(row => this.pickBox(row).checked)
  }

  pickChanged() {
    this.updatePicks()
  }

  pickAll(event) {
    const on = event.currentTarget.checked
    this.pickable().forEach(row => { this.pickBox(row).checked = on })
    this.updatePicks()
  }

  clearPicks() {
    this.rowTargets.forEach(row => { if (this.pickBox(row)) this.pickBox(row).checked = false })
    this.updatePicks()
  }

  updatePicks() {
    if (!this.hasPickBarTarget) return
    const picked = this.pickedRows()
    const n = picked.length
    const ready = this.pickable().length
    if (this.hasPickAllTarget) {
      this.pickAllTarget.checked = n > 0 && n === ready
      this.pickAllTarget.indeterminate = n > 0 && n < ready
      this.pickAllTarget.disabled = ready === 0
    }
    this.pickBarTarget.classList.toggle("hidden", n === 0)
    if (n === 0) return
    const sum = key => picked.reduce((total, row) => total + Number(row.querySelector(`[data-sum-value="${key}"]`)?.dataset.seconds || 0), 0)
    const m = this.messagesValue
    this.pickCountTarget.textContent = (m.picked || "%{count}").replace("%{count}", n)
    const ot = sum("ot")
    this.pickHoursTarget.textContent = `${this.hoursLabel(sum("total"))} h${ot > 0 ? ` · ${this.hoursLabel(ot)} OT` : ""}`
    this.pickApproveTarget.textContent = (m.approve_picked || "%{count}").replace("%{count}", n)
  }

  async approvePicked(event) {
    const rows = this.pickedRows()
    if (!rows.length) return
    const button = event.currentTarget
    button.disabled = true
    let done = 0
    // One at a time: each redraws its own row, and a refusal names its person.
    for (const row of rows) {
      const ok = await this.send(this.approveUrlValue, "POST", {
        user_id: row.dataset.userId, week: this.weekValue,
        submitted_at: row.dataset.submittedAt || undefined, version: row.dataset.version || undefined
      }, row)
      if (ok) done++
    }
    button.disabled = false
    this.updatePicks()
    if (done) {
      const m = this.messagesValue
      this.showToast((done === 1 ? m.approved_one : m.approved_many || "%{count}").replace("%{count}", done))
      track("Timesheet Weeks Approved", { from: "bulk", count: done, failed: rows.length - done })
    }
  }

  // ── Decisions ──────────────────────────────────────────────────────────
  // From the day pop-up too (Phelipe 2026-10-28): rowFor finds the person's row.
  async approve(event) {
    const row = this.rowFor(event.currentTarget)
    if (!row) return
    const button = event.currentTarget
    button.disabled = true
    const ok = await this.send(this.approveUrlValue, "POST", {
      user_id: row.dataset.userId, week: this.weekValue,
      submitted_at: row.dataset.submittedAt || undefined, version: row.dataset.version || undefined
    }, row)
    if (!ok) button.disabled = false
  }

  // One person (the day pop-up) or every ticked week (the bar, data-bulk).
  openSendBack(event) {
    const button = event.currentTarget
    const rows = button.hasAttribute("data-bulk") ? this.pickedRows() : [this.rowFor(button)].filter(Boolean)
    if (!rows.length) return
    this.sendBackRows = rows
    this.sendBackNoteTarget.value = ""
    const names = rows.map(row => row.querySelector("[data-name-toggle]")?.textContent.trim() || "")
    this.sendBackWhoTarget.textContent = names.length > 3 ? (this.messagesValue.people || "%{count}").replace("%{count}", names.length) : names.join(", ")
    this.sendBackModalTarget.classList.remove("hidden")
    this.sendBackNoteTarget.focus()
  }

  closeSendBack() {
    this.sendBackModalTarget.classList.add("hidden")
    this.sendBackRows = null
  }

  async sendBack() {
    const note = this.sendBackNoteTarget.value.trim()
    if (!note) {
      this.showToast(this.messagesValue.note_required)
      this.sendBackNoteTarget.focus()
      return
    }
    const rows = this.sendBackRows
    if (!rows?.length) return
    let done = 0
    for (const row of rows) {
      if (await this.send(this.rejectUrlValue, "POST", { user_id: row.dataset.userId, week: this.weekValue, note }, row)) done++
    }
    if (done) this.closeSendBack()
    if (rows.length > 1) track("Timesheet Weeks Sent Back", { from: "bulk", count: done, failed: rows.length - done })
  }

  // Late fixes need a manager's OK: accept the time as it is.
  async accept(event) {
    const button = event.currentTarget
    const row = this.rowFor(button)
    if (!row) return
    button.disabled = true
    const ok = await this.send(this.acceptUrlValue, "POST", { uuid: button.dataset.uuid, week: this.weekValue }, row)
    if (!ok) button.disabled = false
  }

  // Time typed in or changed by hand (Kloepfer K3-42): OK it from the red
  // flag — the whole week from the row, or one entry from the details.
  async approveEdits(event) {
    const button = event.currentTarget
    const row = this.rowFor(button)
    if (!row) return
    button.disabled = true
    const body = { user_id: row.dataset.userId, week: this.weekValue }
    if (button.dataset.uuid) body.uuid = button.dataset.uuid
    // The note typed in the entry's box, if any (Kloepfer K3-55).
    const note = button.closest("[data-edit-box]")?.querySelector("[data-edit-note]")?.value.trim()
    if (note) body.note = note
    const ok = await this.send(this.approveEditsUrlValue, "POST", body, row)
    if (ok) this.showToast(this.messagesValue.edit_approved)
    else button.disabled = false
  }

  // The small box under an entry (Kloepfer K3-55): a note to go with the
  // approval, or a question for the worker that leaves the week as it is.
  openEditBox(event) {
    const button = event.currentTarget
    // From the entry card, or from a hand edit's line in the history (K3-56).
    const box = (button.closest("[data-edit-scope]") || button.closest("[data-entry]"))?.querySelector("[data-edit-box]")
    if (!box) return
    const mode = button.dataset.mode || "approve"
    box.dataset.mode = mode
    box.querySelectorAll("[data-mode-only]").forEach(el => el.classList.toggle("hidden", el.dataset.modeOnly !== mode))
    box.classList.remove("hidden")
    const field = box.querySelector("[data-edit-note]")
    if (field) {
      field.value = ""
      field.placeholder = mode === "ask" ? (box.dataset.askPlaceholder || "") : (box.dataset.notePlaceholder || "")
      field.focus()
    }
  }

  closeEditBox(event) {
    event.currentTarget.closest("[data-edit-box]")?.classList.add("hidden")
  }

  async ask(event) {
    const button = event.currentTarget
    const box = button.closest("[data-edit-box]")
    const field = box?.querySelector("[data-edit-note]")
    const text = field?.value.trim()
    if (!text) {
      this.showToast(this.messagesValue.question_required)
      field?.focus()
      return
    }
    const row = this.rowFor(button)
    if (!row) return
    button.disabled = true
    const ok = await this.send(this.askUrlValue, "POST", { user_id: row.dataset.userId, week: this.weekValue, uuid: button.dataset.uuid, note: text }, row)
    if (ok) this.showToast(this.messagesValue.question_sent)
    else button.disabled = false
  }

  // ── Employee numbers and crews (admins) ────────────────────────────────
  blur(event) {
    event.preventDefault()
    event.target.blur()
  }

  async saveNumber(event) {
    const row = event.target.closest("tr")
    const data = await this.request(`${this.personUrlValue}`, "PATCH", { user_id: row.dataset.userId, employee_number: event.target.value })
    if (data) {
      event.target.value = data.employee_number || ""
      row.dataset.search = `${row.querySelector("[data-name-toggle]")?.textContent.trim().toLowerCase() || ""} ${(data.employee_number || "").toLowerCase()}`
      this.showToast(this.messagesValue.saved)
    }
  }

  async saveCrew(event) {
    const row = event.target.closest("tr")
    const data = await this.request(`${this.personUrlValue}`, "PATCH", { user_id: row.dataset.userId, crew_id: event.target.value || "" })
    if (data) {
      row.dataset.crewId = data.crew_id ? String(data.crew_id) : ""
      this.showToast(this.messagesValue.saved)
      this.filter()
    }
  }

  openCrews() {
    this.crewsModalTarget.classList.remove("hidden")
    this.newCrewTarget.focus()
  }

  // The crew lists in the rows and the filter come from the server, so a
  // change shows everywhere after one reload.
  closeCrews() {
    this.crewsModalTarget.classList.add("hidden")
    if (this.crewsChanged) window.location.reload()
  }

  // How crews work (Kloepfer K2-5, 2026-09-24). The rows show crews differently
  // per choice, so the page redraws when the pop-up closes, like a crew change.
  async saveCrewMode() {
    if (!this.hasCrewModeTarget) return
    const mode = this.crewModeTarget.querySelector("input[name=ts_crew_mode]:checked")?.value || "fixed"
    if (this.hasCrewSwitchRowTarget) this.crewSwitchRowTarget.classList.toggle("hidden", mode !== "pick")
    const body = { crew_mode: mode }
    if (this.hasCrewSwitchTarget) body.crew_switch_enabled = this.crewSwitchTarget.checked
    const data = await this.request(this.crewModeUrlValue, "PATCH", body)
    if (data) {
      this.crewsChanged = true
      this.showToast(this.messagesValue.saved)
    }
  }

  async addCrew(event) {
    event?.preventDefault()
    const name = this.newCrewTarget.value.trim()
    if (!name) return
    const data = await this.request(this.crewsUrlValue, "POST", { name })
    if (data) {
      this.newCrewTarget.value = ""
      this.drawCrews(data.crews)
    }
  }

  async renameCrew(event) {
    const li = event.target.closest("[data-crew-id]")
    const data = await this.request(`${this.crewsUrlValue}/${li.dataset.crewId}`, "PATCH", { name: event.target.value })
    if (data) this.drawCrews(data.crews)
  }

  async deleteCrew(event) {
    const li = event.target.closest("[data-crew-id]")
    if (!window.confirm(this.messagesValue.delete_crew)) return
    const data = await this.request(`${this.crewsUrlValue}/${li.dataset.crewId}`, "DELETE", {})
    if (data) this.drawCrews(data.crews)
  }

  drawCrews(crews) {
    this.crewsChanged = true
    const template = this.crewsListTarget.querySelector("li")
    this.crewsListTarget.innerHTML = ""
    crews.forEach(crew => {
      const li = document.createElement("li")
      li.className = "flex items-center gap-2"
      li.dataset.crewId = crew.id
      li.innerHTML = template ? template.innerHTML : `<input type="text" maxlength="60" data-action="change->timesheets#renameCrew" class="flex-1 px-2 py-1 border border-gray-300 rounded-lg text-[13px]">`
      li.querySelector("input").value = crew.name
      this.crewsListTarget.appendChild(li)
    })
    this.showToast(this.messagesValue.saved)
  }

  // ── Helpers ────────────────────────────────────────────────────────────
  detailFor(userId) {
    return this.element.querySelector(`tr[data-detail-for="${CSS.escape(String(userId))}"]`)
  }

  // The person's row for a button in their week below it, in the day
  // pop-up, or in the row itself.
  rowFor(el) {
    const detail = el.closest("tr[data-detail-for]")
    if (detail) return detail.previousElementSibling
    const popup = el.closest("[data-day-popup]")
    if (popup) return this.rowTargets.find(row => row.dataset.userId === popup.dataset.userId) || null
    return el.closest("tr")
  }

  // A decision: send it, then swap in the person's fresh row (and reopen
  // their details if they were open).
  async send(url, method, body, row) {
    const data = await this.request(url, method, body)
    if (!data || !data.row_html) return false
    const userId = row.dataset.userId
    const detail = this.detailFor(userId)
    const wasOpen = detail && !detail.classList.contains("hidden")
    // The days that were open stay open after the refresh (Kloepfer K3-58):
    // approving one edit used to fold the day, so the next one meant
    // clicking back in.
    const openDays = wasOpen ? [...detail.querySelectorAll("[data-day]")]
      .filter(section => section.querySelector("[data-day-body]") && !section.querySelector("[data-day-body]").hidden)
      .map(section => section.dataset.day) : []
    const holder = document.createElement("tbody")
    holder.innerHTML = data.row_html
    const [newRow, newDetail] = holder.children
    detail?.remove()
    row.replaceWith(newRow)
    newRow.after(newDetail)
    this.filter()
    if (wasOpen) {
      newDetail.classList.remove("hidden")
      await this.loadDetail(userId)
      // Exactly as they were: the open ones open, the rest closed.
      newDetail.querySelectorAll("[data-day]").forEach(section => this.setDay(section, openDays.includes(section.dataset.day)))
    }
    // The day pop-up, when it shows this person, redraws in place (Phelipe 2026-10-27).
    const popup = this.dayPopup
    if (popup && popup.dataset.userId === String(userId)) await this.loadDay(userId, popup.dataset.date, popup.dataset.sel)
    this.updatePicks()
    return true
  }

  async request(url, method, body) {
    try {
      const res = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: method === "GET" ? undefined : JSON.stringify(body)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this.showToast(data.error || this.messagesValue.failed)
        return null
      }
      return data
    } catch (_e) {
      this.showToast(this.messagesValue.failed)
      return null
    }
  }

  // Times come as ISO stamps and are shown in the viewer's own time zone.
  localizeTimes(root) {
    root.querySelectorAll("time[data-ts]").forEach(el => {
      const d = new Date(el.dataset.ts)
      if (Number.isNaN(d.getTime())) return
      el.dateTime = el.dataset.ts
      el.textContent = el.dataset.fmt === "datetime"
        ? d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    })
  }

  showToast(message) {
    if (!this.hasToastTarget || !message) return
    this.toastTarget.textContent = message
    this.toastTarget.classList.remove("hidden")
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => this.toastTarget.classList.add("hidden"), 3500)
  }

  escape(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
